import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { EvidenceStrength } from "@edufadder/shared-types";
import * as api from "../lib/api.js";
import { ApiError } from "../lib/api.js";
import * as s from "../styles.js";

interface SourceRow {
  citation: string;
  identifier: string;
  role: string;
}

const STRENGTH_OPTIONS: EvidenceStrength[] = ["supported", "mixed_or_contested", "not_adequately_supported", "insufficient_evidence"];

export function NewVersionPage(): JSX.Element {
  const { claimId } = useParams<{ claimId: string }>();
  const navigate = useNavigate();
  const [evidenceStrength, setEvidenceStrength] = useState<EvidenceStrength>("insufficient_evidence");
  const [summary, setSummary] = useState("");
  const [qualifications, setQualifications] = useState("");
  const [alternativeFraming, setAlternativeFraming] = useState("");
  const [sources, setSources] = useState<SourceRow[]>([{ citation: "", identifier: "", role: "" }]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function updateSource(i: number, patch: Partial<SourceRow>): void {
    setSources((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!claimId) return;
    setError(null);
    setSubmitting(true);
    try {
      // Sources must exist (POST /sources) before they can be linked to a
      // version — the API has no "create and attach in one call" shortcut,
      // by design: a source is its own curated record, independent of any
      // one version that cites it, and can be reused across versions.
      const usable = sources.filter((r) => r.citation.trim().length > 0);
      const createdSourceLinks: Array<{ source_id: string; role: string }> = [];
      for (const row of usable) {
        const created = await api.createSource({ citation: row.citation, identifier: row.identifier || null });
        createdSourceLinks.push({ source_id: created.id, role: row.role });
      }

      const version = await api.createVersion(claimId, {
        evidence_strength: evidenceStrength,
        summary,
        qualifications,
        alternative_framing: alternativeFraming,
        source_links: createdSourceLinks,
      });
      navigate(`/claims/${claimId}/versions`, { state: { createdVersionId: version.id } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't create this evidence version.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={s.container}>
      <h1 style={s.heading1}>New evidence version</h1>
      <form onSubmit={(e) => void handleSubmit(e)} style={{ maxWidth: 560 }}>
        <label style={s.label}>
          Evidence status
          <select
            style={s.input}
            value={evidenceStrength}
            onChange={(e) => setEvidenceStrength(e.target.value as EvidenceStrength)}
          >
            {STRENGTH_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </label>
        <label style={s.label}>
          Summary (what the evidence actually shows)
          <textarea style={s.textarea} required value={summary} onChange={(e) => setSummary(e.target.value)} />
        </label>
        <label style={s.label}>
          Qualifications
          <textarea style={s.textarea} value={qualifications} onChange={(e) => setQualifications(e.target.value)} />
        </label>
        <label style={s.label}>
          Alternative framing
          <textarea
            style={s.textarea}
            value={alternativeFraming}
            onChange={(e) => setAlternativeFraming(e.target.value)}
          />
        </label>

        <h2 style={s.heading2}>Sources</h2>
        {sources.map((row, i) => (
          <div key={i} style={{ ...s.card, padding: 12 }}>
            <label style={s.label}>
              Citation
              <input style={s.input} value={row.citation} onChange={(e) => updateSource(i, { citation: e.target.value })} />
            </label>
            <label style={s.label}>
              Identifier (DOI/ISBN/URL)
              <input style={s.input} value={row.identifier} onChange={(e) => updateSource(i, { identifier: e.target.value })} />
            </label>
            <label style={s.label}>
              Role in this version (e.g. "systematic review", "rebuttal")
              <input style={s.input} value={row.role} onChange={(e) => updateSource(i, { role: e.target.value })} />
            </label>
          </div>
        ))}
        <button
          type="button"
          style={s.buttonSecondary}
          onClick={() => setSources((rows) => [...rows, { citation: "", identifier: "", role: "" }])}
        >
          Add another source
        </button>

        {error && <p style={s.errorText}>{error}</p>}
        <div style={{ marginTop: 16 }}>
          <button type="submit" style={s.buttonPrimary} disabled={submitting}>
            {submitting ? "Saving…" : "Save as draft"}
          </button>
        </div>
        <p style={s.mutedText}>
          Newly-added sources start out <strong>unverified</strong> — someone other than you will need to verify them
          before this version can be approved. Newly-created sources are added fresh each time (there is no "reuse an
          existing source" picker yet), so if a source is already in the system, re-adding its citation here creates a
          second record for it.
        </p>
      </form>
    </div>
  );
}
